import { Button } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { BillingPayment } from './billing-payment.tsx';

/** Account-only setup. onSaved continues a user-submitted action, never merely opening the form. */
export function PaymentMethodSetup({
  accountId,
  submitLabel,
  onSaved,
  onPendingChange,
}: {
  accountId: string;
  submitLabel?: string;
  onSaved: () => void;
  onPendingChange?: (pending: boolean) => void;
}) {
  const started = useRef(false);
  const notified = useRef(false);
  const [submitted, setSubmitted] = useState(false);
  const prepare = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/accounts/{accountUserId}/payment-method', {
          params: { path: { accountUserId: accountId } },
          body: { idempotencyKey: crypto.randomUUID() },
        }),
      ),
  });
  const start = prepare.mutate;
  useEffect(() => {
    if (!started.current) {
      started.current = true;
      start();
    }
  }, [start]);
  const operationId = prepare.data?.id;
  const completion = useQuery({
    queryKey: ['setup-completion', accountId, operationId],
    enabled: Boolean(
      operationId && (submitted || ['Pending', 'Processing'].includes(prepare.data?.status ?? '')),
    ),
    queryFn: async () =>
      unwrap(
        await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/refresh', {
          params: { path: { accountUserId: accountId, operationId: operationId! } },
          body: {},
        }),
      ),
    refetchInterval: (query) =>
      ['Applied', 'Canceled', 'Failed'].includes(query.state.data?.status ?? '') ||
      (!submitted && query.state.data?.status === 'AwaitingPaymentMethod')
        ? false
        : 2000,
  });
  useEffect(() => {
    if (!submitted || completion.data?.status !== 'Applied' || notified.current) return;
    notified.current = true;
    void queryClient.invalidateQueries({ queryKey: ['billing', accountId] });
    onPendingChange?.(false);
    onSaved();
  }, [submitted, completion.data?.status, accountId, onSaved, onPendingChange]);
  const status = completion.data?.status ?? prepare.data?.status;
  const closed = status === 'Canceled' || status === 'Failed';
  const error = prepare.error ?? completion.error;
  const waiting = status === 'Pending' || status === 'Processing';
  return (
    <div className="grid gap-4">
      <ErrorNotice error={error} />
      {closed ? (
        <>
          <p>Payment setup could not be completed. Please try again.</p>
          <Button
            onClick={() => {
              setSubmitted(false);
              onPendingChange?.(false);
              start();
            }}
          >
            Try again
          </Button>
        </>
      ) : prepare.isError ? (
        <Button onClick={() => start()}>Try again</Button>
      ) : !operationId || waiting ? (
        <Loading label={status === 'Processing' ? 'Verifying payment method…' : 'Preparing payment form…'} />
      ) : submitted ? (
        <Loading label="Saving payment method…" />
      ) : (
        <BillingPayment
          accountId={accountId}
          operationId={operationId}
          submitLabel={submitLabel ?? 'Save payment method'}
          requireSubmission
          onPendingChange={onPendingChange}
          onComplete={() => setSubmitted(true)}
        />
      )}
    </div>
  );
}
